import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plane, Eye, EyeOff, Loader2, LogIn, Lock } from 'lucide-react';

const ClientLoginPage = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { clientLogin } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await clientLogin(form.email, form.password);
      toast.success(`Bienvenido ${userData.name}!`);
      navigate('/cliente');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Credenciales incorrectas';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #064e3b 0%, #0f172a 100%)' }}>
      <div className="relative w-full max-w-md">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8" style={{ animation: 'fadeIn 0.5s ease-out' }}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500 rounded-2xl mb-4 shadow-lg">
              <Plane className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Aircom Connect</h1>
            <p className="text-slate-500 mt-1">Portal de Clientes</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700">Correo electronico</Label>
              <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="tu@email.com" required className="h-11 bg-slate-50" data-testid="client-login-email" />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Contrasena</Label>
              <div className="relative">
                <Input name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={handleChange} placeholder="Tu contrasena" required className="h-11 bg-slate-50 pr-10" data-testid="client-login-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="client-login-submit">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-4 h-4" /> Iniciar sesion
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 p-3 bg-slate-50 rounded-lg border border-slate-200 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Lock className="w-4 h-4" />
              <span>El registro esta restringido. Contacte al administrador.</span>
            </div>
          </div>

          <div className="mt-4 text-center">
            <Link to="/login" className="text-xs text-slate-400 hover:text-slate-600">
              Acceso para gestores y administradores
            </Link>
          </div>
        </div>

        <p className="text-center text-white/60 text-sm mt-6">
          &copy; 2024 Aircom Connect. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default ClientLoginPage;
